import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Gallery.css';

interface GalleryImage {
  src: string;
  alt: string;
}

const galleryImages: GalleryImage[] = [
  { src: '/images/general/competition-group.jpg', alt: 'Students competing at a Taekwon-Do tournament' },
  { src: '/images/general/competition-sparring.jpg', alt: 'Sparring action at competition' },
  { src: '/images/general/competition-high-kick.jpg', alt: 'High kick performance at competition' },
  { src: '/images/general/competition-medal-ceremony.jpg', alt: 'Medal ceremony presentation' },
  { src: '/images/general/aeta-medals.jpg', alt: 'AETA gold, silver and bronze medals' },
  { src: '/images/general/officials-selfie.jpg', alt: 'Club officials at competition' },
  { src: '/images/general/aeta-grading.jpg', alt: 'Grading ceremony — receiving a belt' },
  { src: '/images/general/class-demonstration.jpg', alt: 'Instructor demonstrating technique' },
  { src: '/images/general/kids-class-helmets.jpg', alt: "Children's class with sparring helmets" },
  { src: '/images/general/seminar-demonstration.jpg', alt: 'Seminar demonstration with large group' },
  { src: '/images/general/training-class-squats.jpg', alt: 'Training class practising together' },
  { src: '/images/general/kid-kicking-pad.jpg', alt: 'Young student kicking a pad' },
  { src: '/images/general/five-tenets.jpg', alt: 'The 5 Tenets of Taekwon-Do' },
  { src: '/images/general/respect-bowing.jpg', alt: 'Respect — Martial Arts teaches why not to fight' },
  { src: '/images/general/tkd-girls-quote.jpg', alt: 'To Taekwondo Girls Everywhere' },
  { src: '/images/general/white-belt-quote.jpg', alt: 'White Belt — a higher rank than sitting in a chair' },
  { src: '/images/general/i-want-to-quit.jpg', alt: "A parent's perspective on pushing through challenges" },
  { src: '/images/general/protect-them.jpg', alt: 'Teach them to protect themselves' },
  { src: '/images/general/new-term.jpg', alt: 'New Term — time to get training!' },
  { src: '/images/general/having-black-belt-quote.jpg', alt: 'Having a black belt is not the same as being a black belt' },
  { src: '/images/general/belt-goal-quote.jpg', alt: 'If knowledge is your goal, you will never stop training' },
];

const Gallery: React.FC = () => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => prev !== null ? (prev + 1) % galleryImages.length : null);
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => prev !== null ? (prev - 1 + galleryImages.length) % galleryImages.length : null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  return (
    <section className="gallery-section">
      <div className="gallery-container">
        <h1 className="gallery-page-title">Gallery</h1>
        <p className="gallery-subtitle">A snapshot of life at Weald Taekwon-Do — training, competing, and growing together.</p>
        <div className="gallery-grid">
          {galleryImages.map((img, index) => (
            <div
              key={img.src}
              className="gallery-item"
              onClick={() => setLightboxIndex(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(index)}
              aria-label={`View: ${img.alt}`}
            >
              <img src={img.src} alt={img.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && ReactDOM.createPortal(
        <div
          className="gallery-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="lightbox-close"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
          >×</button>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
            aria-label="Previous image"
          >‹</button>
          <img
            src={galleryImages[lightboxIndex].src}
            alt={galleryImages[lightboxIndex].alt}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
            aria-label="Next image"
          >›</button>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Gallery;
